import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { DocumentArrowUpIcon, DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export default function FileUpload({ onFileSelect, accept = '.pdf,.txt,.json', multiple = false, className = '' }) {
  const [selectedFile, setSelectedFile] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      if (multiple) {
        onFileSelect(acceptedFiles);
        setSelectedFile(acceptedFiles[0]);
      } else {
        onFileSelect(acceptedFiles[0]);
        setSelectedFile(acceptedFiles[0]);
      }
    }
  }, [onFileSelect, multiple]);

  const handleClear = (e) => {
    e.stopPropagation();
    onFileSelect(null);
    setSelectedFile(null);
  };

  // Convert accept prop to dropzone format
  const getAcceptObject = (acceptString) => {
    const acceptObj = {};
    if (acceptString.includes('.pdf')) {
      acceptObj['application/pdf'] = ['.pdf'];
    }
    if (acceptString.includes('.txt')) {
      acceptObj['text/plain'] = ['.txt'];
    }
    if (acceptString.includes('.json')) {
      acceptObj['application/json'] = ['.json'];
    }
    if (acceptString.includes('.docx')) {
      acceptObj['application/vnd.openxmlformats-officedocument.wordprocessingml.document'] = ['.docx'];
    }
    if (acceptString.includes('.doc')) {
      acceptObj['application/msword'] = ['.doc'];
    }
    if (acceptString.includes('.pptx')) {
      acceptObj['application/vnd.openxmlformats-officedocument.presentationml.presentation'] = ['.pptx'];
    }
    return acceptObj;
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: getAcceptObject(accept),
    multiple,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const truncatedName = selectedFile && selectedFile.name.length > 40
    ? selectedFile.name.substring(0, 37) + '...'
    : selectedFile?.name;

  return (
    <div className={`w-full ${className}`}>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-indigo-400 bg-indigo-50'
            : isDragReject
            ? 'border-red-400 bg-red-50'
            : selectedFile
            ? 'border-[var(--vault-border,#374151)] bg-[var(--vault-card,#1f2937)]'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input {...getInputProps()} />
        {selectedFile ? (
          <div className="flex items-center justify-center space-x-3">
            <DocumentTextIcon className="w-10 h-10 text-[var(--spotlight-gold,#d4a843)] flex-shrink-0" />
            <div className="text-left min-w-0">
              <p className="text-sm font-medium text-[var(--spotlight-gold,#d4a843)] truncate" title={selectedFile.name}>
                {truncatedName}
              </p>
              <p className="text-xs text-[var(--text-secondary,#9ca3af)]">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded-full hover:bg-gray-700/50 transition-colors flex-shrink-0"
              title="Remove file"
            >
              <XMarkIcon className="w-5 h-5 text-[var(--text-secondary,#9ca3af)] hover:text-[var(--text-primary,#f3f4f6)]" />
            </button>
          </div>
        ) : isDragActive ? (
          <div>
            <DocumentArrowUpIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-indigo-600 font-medium">Drop the file here...</p>
          </div>
        ) : (
          <div>
            <DocumentArrowUpIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 font-medium mb-2">
              Drag and drop a file here, or click to select
            </p>
            <p className="text-sm text-gray-500">
              Supports {accept.replace(/\./g, '').replace(/,/g, ', ').toUpperCase()} files (max 50MB)
            </p>
          </div>
        )}
      </div>
    </div>
  );
}